import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, LogOut, Eye, Mail, MapPin, MessageSquare } from 'lucide-react';
import ArticleEditor from '../components/ArticleEditor';
import ImageUpload from '../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('viaggi');
  const [travels, setTravels] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTravel, setEditingTravel] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    titolo: '', destinazione: '', payoff: '',
    data_viaggio: '', durata: '', eta_bambini: '',
    content: '', difficolta: '', stagione_consigliata: '',
    cover_image: '', categoria: '', published: true
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin');
  }, [isAuthenticated, authLoading, navigate]);

  const fetchTravels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/travels?published_only=false`);
      setTravels(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei viaggi');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscribers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter`);
      setSubscribers(r.data);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contacts`);
      setContacts(r.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTravels();
      fetchSubscribers();
      fetchContacts();
    }
  }, [isAuthenticated, fetchTravels, fetchSubscribers, fetchContacts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTravel) {
        await axios.put(`${API}/travels/${editingTravel.slug}`, formData);
        toast.success('Viaggio aggiornato con successo!');
      } else {
        await axios.post(`${API}/travels`, formData);
        toast.success('Viaggio creato con successo!');
      }
      setDialogOpen(false);
      resetForm();
      fetchTravels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleEdit = (travel) => {
    setEditingTravel(travel);
    setFormData({
      titolo: travel.titolo,
      destinazione: travel.destinazione,
      payoff: travel.payoff,
      data_viaggio: travel.data_viaggio,
      durata: travel.durata,
      eta_bambini: travel.eta_bambini,
      content: travel.content || '',
      difficolta: travel.difficolta,
      stagione_consigliata: travel.stagione_consigliata,
      cover_image: travel.cover_image,
      categoria: travel.categoria || '',
      published: travel.published
    });
    setDialogOpen(true);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Sei sicura di voler eliminare questo viaggio?')) return;
    try {
      await axios.delete(`${API}/travels/${slug}`);
      toast.success('Viaggio eliminato con successo!');
      fetchTravels();
    } catch { toast.error("Errore nell'eliminazione del viaggio"); }
  };

  const resetForm = () => {
    setEditingTravel(null);
    setFormData({
      titolo: '', destinazione: '', payoff: '',
      data_viaggio: '', durata: '', eta_bambini: '',
      content: '', difficolta: '', stagione_consigliata: '',
      cover_image: '', categoria: '', published: true
    });
  };

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla newsletter?`)) return;
    try {
      await axios.delete(`${API}/newsletter/${encodeURIComponent(email)}`);
      toast.success('Iscritto rimosso!');
      fetchSubscribers();
    } catch { toast.error('Errore nella rimozione'); }
  };

  const handleLogout = () => { logout(); navigate('/admin'); };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <p>Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-semibold" data-testid="dashboard-title">Dashboard Admin</h1>
            <p className="text-sm text-foreground/60">Benvenuta, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="view-site-button">
              <Eye size={18} className="mr-2" />Vedi sito
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
              <LogOut size={18} className="mr-2" />Esci
            </Button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 border-b border-border">
            {[
              { id: 'viaggi', icon: MapPin, label: 'Viaggi', count: travels.length },
              { id: 'newsletter', icon: Mail, label: 'Newsletter', count: subscribers.length },
              { id: 'messaggi', icon: MessageSquare, label: 'Messaggi', count: contacts.length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-foreground/60 hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {activeTab === 'viaggi' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-3xl" data-testid="travels-section-title">I tuoi viaggi</h2>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-travel-button"><Plus size={18} className="mr-2" />Nuovo viaggio</Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl w-full h-[92vh] flex flex-col p-0 gap-0">
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
                    <DialogTitle data-testid="dialog-title">
                      {editingTravel ? 'Modifica viaggio' : 'Nuovo viaggio'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Compila i campi per gestire il viaggio</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" data-testid="travel-form">
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-lenis-prevent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Titolo *</label>
                          <Input value={formData.titolo} onChange={(e) => setFormData({ ...formData, titolo: e.target.value })} required data-testid="form-titolo" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Destinazione *</label>
                          <Input value={formData.destinazione} onChange={(e) => setFormData({ ...formData, destinazione: e.target.value })} required data-testid="form-destinazione" />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-1 block">Payoff (frase breve) *</label>
                        <Input value={formData.payoff} onChange={(e) => setFormData({ ...formData, payoff: e.target.value })} placeholder="Una frase evocativa che riassume il viaggio" required data-testid="form-payoff" />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Data viaggio *</label>
                          <Input value={formData.data_viaggio} onChange={(e) => setFormData({ ...formData, data_viaggio: e.target.value })} placeholder="Es: Agosto 2025" required data-testid="form-data-viaggio" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Durata *</label>
                          <Input value={formData.durata} onChange={(e) => setFormData({ ...formData, durata: e.target.value })} placeholder="Es: 10 giorni" required data-testid="form-durata" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Età bambini *</label>
                          <Input value={formData.eta_bambini} onChange={(e) => setFormData({ ...formData, eta_bambini: e.target.value })} placeholder="Es: 2 e 5 anni" required data-testid="form-eta-bambini" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Difficoltà *</label>
                          <Input value={formData.difficolta} onChange={(e) => setFormData({ ...formData, difficolta: e.target.value })} placeholder="Es: Media, facile per famiglie" required data-testid="form-difficolta" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Stagione consigliata *</label>
                          <Input value={formData.stagione_consigliata} onChange={(e) => setFormData({ ...formData, stagione_consigliata: e.target.value })} placeholder="Es: Primavera ed estate" required data-testid="form-stagione" />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-2 block">Immagine copertina</label>
                        <ImageUpload value={formData.cover_image} onChange={(url) => setFormData({ ...formData, cover_image: url })} />
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-1 block">Categoria</label>
                        <select
                          value={formData.categoria}
                          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                          className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="form-categoria"
                        >
                          <option value="">Seleziona categoria</option>
                          <option value="europa">Europa</option>
                          <option value="america">America</option>
                          <option value="asia">Asia</option>
                          <option value="africa">Africa</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-2 block">Contenuto del viaggio</label>
                        <ArticleEditor value={formData.content} onChange={content => setFormData({ ...formData, content })} />
                      </div>

                      <div className="flex items-center gap-2 pb-2">
                        <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} id="published" data-testid="form-published" />
                        <label htmlFor="published" className="text-sm font-medium">Pubblica subito</label>
                      </div>
                    </div>

                    <div className="flex gap-3 px-6 py-4 border-t border-border flex-shrink-0 bg-background">
                      <Button type="submit" className="flex-1" data-testid="form-submit">
                        {editingTravel ? 'Aggiorna' : 'Crea viaggio'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} data-testid="form-cancel">
                        Annulla
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {travels.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-travels">
                <p className="text-foreground/60 mb-4">Nessun viaggio ancora</p>
                <p className="text-sm text-foreground/40">Clicca su "Nuovo viaggio" per iniziare</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="travels-list">
                {travels.map((travel) => (
                  <Card key={travel.slug} className="overflow-hidden" data-testid={`travel-card-${travel.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img src={travel.cover_image} alt={travel.titolo} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-serif text-xl font-medium">{travel.titolo}</h3>
                        {!travel.published && <span className="text-xs bg-muted px-2 py-1 rounded">Bozza</span>}
                      </div>
                      <p className="text-sm text-foreground/60 mb-4">{travel.destinazione}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(travel)} className="flex-1" data-testid={`edit-button-${travel.slug}`}>
                          <Edit size={16} className="mr-1" />Modifica
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(travel.slug)} data-testid={`delete-button-${travel.slug}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'newsletter' && (
          <div data-testid="newsletter-section">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-3xl">Iscritti Newsletter</h2>
                <p className="text-sm text-foreground/60 mt-1">{subscribers.length} iscritti totali</p>
              </div>
            </div>
            {subscribers.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-subscribers">
                <Mail size={32} className="mx-auto mb-4 text-foreground/30" />
                <p className="text-foreground/60 mb-2">Nessun iscritto ancora</p>
              </Card>
            ) : (
              <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="subscribers-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium text-foreground/70">Email</th>
                      <th className="text-left px-6 py-3 font-medium text-foreground/70">Nome</th>
                      <th className="text-left px-6 py-3 font-medium text-foreground/70">Data iscrizione</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, i) => (
                      <tr key={sub.email} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/30'}`} data-testid={`subscriber-row-${i}`}>
                        <td className="px-6 py-4 font-medium">{sub.email}</td>
                        <td className="px-6 py-4 text-foreground/70">{sub.nome || '—'}</td>
                        <td className="px-6 py-4 text-foreground/60">
                          {new Date(sub.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleDeleteSubscriber(sub.email)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-subscriber-${i}`}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'messaggi' && (
          <div data-testid="messaggi-section">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-serif text-3xl">Messaggi ricevuti</h2>
                <p className="text-sm text-foreground/60 mt-1">{contacts.length} messaggi totali</p>
              </div>
            </div>
            {contacts.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-contacts">
                <MessageSquare size={32} className="mx-auto mb-4 text-foreground/30" />
                <p className="text-foreground/60">Nessun messaggio ancora</p>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="contacts-list">
                {contacts.map((msg, i) => (
                  <Card key={`${msg.email}-${msg.created_at}`} className="p-6" data-testid={`contact-row-${i}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-medium text-sm">{msg.nome}</span>
                          <span className="text-xs text-foreground/50">·</span>
                          <a href={`mailto:${msg.email}`} className="text-xs text-primary hover:underline">{msg.email}</a>
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{msg.messaggio}</p>
                      </div>
                      <span className="text-xs text-foreground/40 flex-shrink-0">
                        {new Date(msg.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
